/**
 * ðŸ† GAMIFICATION BADGE â€” Badge de Autoridade por Organização
 * 
 * Componente que exibe o badge do usuário (Bronze → Black Belt)
 * isolado por organização.
 * 
 * Design: Premium Industrial com glassmorphism e animações sutis
 */

import React, { useEffect, useState } from 'react';
import { Award, TrendingUp, Zap, Crown, Sparkles } from 'lucide-react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { supabase } from '../../lib/supabaseClient';
import type { AuthorityLevel } from '../UserAuthorityBadge';

/** @deprecated Use AuthorityLevel from UserAuthorityBadge instead */
export type BeltLevel = AuthorityLevel;

interface BadgeData {
  level: AuthorityLevel;
  points: number;
  nextLevelPoints: number;
  rank: number;
  totalUsers: number;
}

interface GamificationBadgeProps {
  userId?: string;
  variant?: 'compact' | 'full';
  showProgress?: boolean;
  className?: string;
}

const BELT_CONFIG: Record<AuthorityLevel, { 
  name: string; 
  icon: any; 
  color: string; 
  bgGradient: string;
  minPoints: number;
}> = {
  bronze: {
    name: 'Bronze Belt',
    icon: Award,
    color: 'text-orange-700 dark:text-orange-400',
    bgGradient: 'from-orange-100 to-amber-100 dark:from-orange-950/30 dark:to-amber-950/30',
    minPoints: 0,
  },
  silver: {
    name: 'Silver Belt',
    icon: TrendingUp,
    color: 'text-slate-600 dark:text-slate-300',
    bgGradient: 'from-slate-100 to-gray-100 dark:from-slate-900/30 dark:to-gray-900/30',
    minPoints: 1000,
  },
  gold: {
    name: 'Gold Belt',
    icon: Zap,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgGradient: 'from-yellow-100 to-amber-100 dark:from-yellow-950/30 dark:to-amber-950/30',
    minPoints: 5000,
  },
  black_belt: {
    name: 'Black Belt',
    icon: Crown,
    color: 'text-purple-700 dark:text-purple-400',
    bgGradient: 'from-purple-100 to-indigo-100 dark:from-purple-950/30 dark:to-indigo-950/30',
    minPoints: 15000,
  },
};

function calculateLevel(points: number): AuthorityLevel {
  if (points >= 15000) return 'black_belt';
  if (points >= 5000)  return 'gold';
  if (points >= 1000)  return 'silver';
  return 'bronze';
}

function getNextLevelPoints(level: AuthorityLevel): number {
  const levels: AuthorityLevel[] = ['bronze', 'silver', 'gold', 'black_belt'];
  const currentIndex = levels.indexOf(level);
  if (currentIndex === levels.length - 1) return BELT_CONFIG[level].minPoints; // Nível máximo
  const nextLevel = levels[currentIndex + 1];
  return BELT_CONFIG[nextLevel].minPoints;
}

export const GamificationBadge: React.FC<GamificationBadgeProps> = ({
  userId,
  variant = 'compact',
  showProgress = true,
  className = '',
}) => {
  const { workspace } = useWorkspace();
  const [badgeData, setBadgeData] = useState<BadgeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId || !workspace) {
      setIsLoading(false);
      return;
    }

    // Validar se organizationId é um UUID válido (silenciosamente)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(workspace.organizationId)) {
      // Dev mode - retornar estado padrão sem log
      setBadgeData({
        level: 'bronze',
        points: 0,
        nextLevelPoints: 1000,
        rank: 0,
        totalUsers: 0,
      });
      setIsLoading(false);
      return;
    }

    const fetchBadgeData = async () => {
      try {
        // Buscar pontos do usuário na tabela oficial user_points
        const { data: userPointsData, error } = await supabase
          .from('user_points')
          .select('total_points, authority_level')
          .eq('user_id', userId)
          .eq('organization_id', workspace.organizationId)
          .maybeSingle();

        if (error) {
          console.warn('[GamificationBadge] Erro ao buscar dados:', error.message);
          // Fallback: usuário sem pontos ainda
          setBadgeData({
            level: 'bronze',
            points: 0,
            nextLevelPoints: 1000,
            rank: 0,
            totalUsers: 0,
          });
          return;
        }

        const points = userPointsData?.total_points ?? 0;
        // Preferir authority_level do banco; calcular localmente como fallback
        const level: AuthorityLevel = userPointsData?.authority_level ?? calculateLevel(points);
        const nextLevelPoints = getNextLevelPoints(level);

        setBadgeData({
          level,
          points,
          nextLevelPoints,
          rank: 0,
          totalUsers: 0,
        });
      } catch (err) {
        console.error('[GamificationBadge] Erro inesperado:', err);
        setBadgeData({
          level: 'bronze',
          points: 0,
          nextLevelPoints: 1000,
          rank: 0,
          totalUsers: 0,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBadgeData();
  }, [userId, workspace]);

  if (isLoading || !badgeData || !workspace) return null;

  const config = BELT_CONFIG[badgeData.level];
  const Icon = config.icon;
  const progress = badgeData.level === 'black_belt'
    ? 100
    : ((badgeData.points - config.minPoints) / (badgeData.nextLevelPoints - config.minPoints)) * 100;

  // Variant Compact (para header)
  if (variant === 'compact') {
    return (
      <div 
        className={`flex items-center gap-2 px-3 py-2 bg-gradient-to-r ${config.bgGradient} rounded border border-slate-200 dark:border-slate-700 shadow-sm ${className}`}
        title={`${config.name} - ${badgeData.points} pontos`}
      >
        <Icon size={16} className={config.color} />
        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
          {badgeData.points}
        </span>
        <Sparkles size={12} className="text-slate-400 dark:text-slate-500" />
      </div>
    );
  }

  // Variant Full (para dashboard / perfil)
  return (
    <div
      className={`bg-gradient-to-b from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-900/60 rounded-sm border border-slate-200 dark:border-slate-700 p-4 min-w-[220px] ${className}`}
    >
      {/* ── Level header ─────────────────────────────── */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-sm border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex items-center justify-center">
          <Icon size={20} className={config.color} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 leading-none mb-1">
            Nível de Autoridade
          </p>
          <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 leading-none truncate">
            {config.name}
          </p>
        </div>
      </div>

      {/* ── Points ───────────────────────────────────── */}
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
          Pontuação
        </p>
        <p className="text-[15px] font-bold tabular-nums text-slate-800 dark:text-slate-100 leading-none">
          {badgeData.points.toLocaleString('pt-BR')}
          <span className="text-[10px] font-normal text-slate-400 ml-0.5">pts</span>
        </p>
      </div>

      {/* ── Progress ─────────────────────────────────── */}
      {showProgress && badgeData.level !== 'black_belt' && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-slate-500 dark:text-slate-400">
              Próximo nível
            </p>
            <span className="text-[11px] font-semibold tabular-nums text-slate-700 dark:text-slate-300">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${Math.max(2, progress)}%` }}
            />
          </div>
          <p className="text-[10px] text-slate-400 dark:text-slate-500 tabular-nums">
            {(badgeData.nextLevelPoints - badgeData.points).toLocaleString('pt-BR')} pts restantes
          </p>
        </div>
      )}

      {badgeData.level === 'black_belt' && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-sm">
          <Crown size={11} className="text-purple-600 dark:text-purple-400 flex-shrink-0" />
          <p className="text-[10px] font-semibold text-purple-700 dark:text-purple-400">
            Nível Máximo Atingido
          </p>
        </div>
      )}

      {/* ── Org footer ───────────────────────────────── */}
      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
        <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
          <span className="text-slate-300 dark:text-slate-600">Org: </span>
          <span className="font-medium text-slate-600 dark:text-slate-400">
            {workspace.organizationName}
          </span>
        </p>
      </div>
    </div>
  );
};
